import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MutationFunction } from 'react-apollo';
import { Form as FormikForm, Formik } from 'formik';
import { pick } from 'lodash';

import {
  GetManualNotificationsDocument,
  ManualNotificationsItem,
  ManualNotificationsItemUpdateInput,
  ManualNotificationType,
  QueryGetManualNotificationItemsArgs,
  UpdateManualNotificationComponent,
  UserSegment,
} from 'api';
import { useNotify } from 'hooks';
import { NotificationSchema } from 'features/admin/validations';
import { updateMessage } from 'apollo/reducers';

import Form from './Form';

interface IProps {
  item: ManualNotificationsItem;
  segments: UserSegment[];
  queryVariables: QueryGetManualNotificationItemsArgs;
  onEdit?: () => void;
}

const EditMessage: FC<IProps> = ({ segments, item, onEdit, queryVariables }) => {
  const [working, setWorking] = useState(false);
  const { t } = useTranslation();
  const notify = useNotify();

  function getEditData(values: ManualNotificationsItemUpdateInput) {
    return pick(
      values,
      'id',
      'entire_company',
      'notification_type',
      'send_at',
      'segment_ids',
      'format',
      'message',
      'email',
    );
  }

  function handleError() {
    setWorking(false);
    notify.mutationError();
  }

  function handleCompleted() {
    setWorking(false);
    onEdit();
    notify.info({ message: t('messages:updateManualNotificationMessageSuccess') });
  }

  function handleSubmit(mutation: MutationFunction, values: ManualNotificationsItemUpdateInput) {
    // Typing in URL field and clearing it leaves an empty string
    values.message.url = values.message.url || null;
    setWorking(true);

    return mutation({
      variables: { data: getEditData(values) },
      update: (cache, result) => {
        if (values.notification_type === ManualNotificationType.Scheduled) {
          return;
        }

        updateMessage({
          cache,
          result,
          query: GetManualNotificationsDocument,
          variables: queryVariables,
        });
      },
    });
  }

  return (
    <UpdateManualNotificationComponent onError={handleError} onCompleted={handleCompleted}>
      {mutation => (
        <Formik
          onSubmit={values => handleSubmit(mutation, values)}
          initialValues={item}
          validationSchema={NotificationSchema}>
          {() => (
            <FormikForm>
              <Form
                titleI18nKey="messages:updateCompanyMessage"
                segments={segments}
                working={working}
                onEdit={onEdit}
              />
            </FormikForm>
          )}
        </Formik>
      )}
    </UpdateManualNotificationComponent>
  );
};

export default EditMessage;
